import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User, Minimize2, RotateCcw, AlertCircle, Wifi, WifiOff } from 'lucide-react';
import { useChat } from '../../hooks/useChat';
import { ChatMessage } from '../../types/chat';
import { cn } from '../../utils/cn';

const AdhaChat = () => {
  const [message, setMessage] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const {
    state,
    toggleChat,
    minimizeChat,
    sendMessage,
    resetConversation,
    isLoading,
    error,
    clearError,
  } = useChat();

  // Détection mobile et responsive
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-scroll vers le bas quand de nouveaux messages arrivent
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [state.currentConversation?.messages]);

  // Focus sur l'input quand le chat s'ouvre
  useEffect(() => {
    if (state.isOpen && !state.isMinimized) {
      inputRef.current?.focus();
    }
  }, [state.isOpen, state.isMinimized]);

  const handleSend = async () => {
    if (message.trim() && !isLoading) {
      const messageToSend = message.trim();
      setMessage('');
      await sendMessage(messageToSend);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTimestamp = (timestamp: any) => {
    try {
      // Convertir en Date si nécessaire
      let date: Date;
      
      if (!timestamp) {
        return '--:--';
      }
      
      if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else {
        console.warn('Type de timestamp non supporté:', typeof timestamp, timestamp);
        return '--:--';
      }
      
      // Vérifier si la date est valide
      if (isNaN(date.getTime())) {
        return '--:--';
      }
      
      return new Intl.DateTimeFormat('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch (error) {
      console.warn('Erreur de formatage timestamp:', error, timestamp);
      return '--:--';
    }
  };

  const renderMessage = (msg: ChatMessage) => (
    <div
      key={msg.id}
      className={cn(
        "flex gap-3 mb-4 animate-fade-in",
        msg.isBot ? "justify-start" : "justify-end"
      )}
    >
      {msg.isBot && (
        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-primary to-primary-600 flex items-center justify-center flex-shrink-0">
          <Bot className="w-4 h-4 text-white" />
        </div>
      )}
      
      <div className={cn(
        "max-w-[80%] rounded-2xl px-4 py-3 relative",
        msg.isBot 
          ? "bg-gray-100 text-gray-800" 
          : "bg-gradient-to-r from-primary to-primary-600 text-white"
      )}>
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
        <div className={cn(
          "text-xs mt-2 flex items-center gap-1",
          msg.isBot ? "text-gray-500" : "text-white/70"
        )}>
          <span>{formatTimestamp(msg.timestamp)}</span>
          {!msg.isBot && msg.status && (
            <>
              {msg.status === 'sending' && <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />}
              {msg.status === 'sent' && <div className="w-2 h-2 bg-green-500 rounded-full" />}
              {msg.status === 'error' && <AlertCircle className="w-3 h-3 text-red-500" />}
            </>
          )}
        </div>
      </div>

      {!msg.isBot && (
        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-success to-green-600 flex items-center justify-center flex-shrink-0">
          <User className="w-4 h-4 text-white" />
        </div>
      )}
    </div>
  );

  // État fermé : bouton flottant
  if (!state.isOpen) {
    return (
      <div className={cn(
        "fixed z-40",
        isMobile ? "bottom-4 right-4" : "bottom-6 right-6"
      )}>
        <button
          onClick={toggleChat}
          className={cn(
            "relative bg-gradient-to-r from-primary to-primary-600 text-white p-4 rounded-full shadow-lg",
            "hover:shadow-xl transition-all duration-300 hover:scale-105",
            "focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2"
          )}
          aria-label="Ouvrir le chat Adha"
        >
          <MessageCircle className="w-6 h-6" />
          {state.unreadCount > 0 && (
            <div className="absolute -top-2 -right-2 bg-warning text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
              {state.unreadCount > 9 ? '9+' : state.unreadCount}
            </div>
          )}
          {!state.isConnected && (
            <div className="absolute -top-1 -left-1 w-3 h-3 bg-red-500 rounded-full animate-ping" />
          )}
        </button>
      </div>
    );
  }

  // État minimisé
  if (state.isMinimized) {
    return (
      <div className={cn(
        "fixed z-40",
        isMobile ? "bottom-4 right-4" : "bottom-6 right-6"
      )}>
        <div className="bg-white rounded-t-2xl shadow-xl border border-gray-200 min-w-[280px]">
          <div className="bg-gradient-to-r from-primary to-primary-600 text-white p-3 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="w-3 h-3" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">Adha</h4>
                <div className="flex items-center gap-1 text-xs text-white/80">
                  {state.isConnected ? (
                    <>
                      <Wifi className="w-3 h-3" />
                      <span>En ligne</span>
                    </>
                  ) : (
                    <>
                      <WifiOff className="w-3 h-3" />
                      <span>Hors ligne</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={minimizeChat}
                className="text-white/80 hover:text-white transition-colors p-1"
                aria-label="Développer le chat"
              >
                <MessageCircle className="w-4 h-4" />
              </button>
              <button
                onClick={toggleChat}
                className="text-white/80 hover:text-white transition-colors p-1"
                aria-label="Fermer le chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // État ouvert : interface complète
  return (
    <div className={cn(
      "fixed z-40 max-h-[calc(100vh-2rem)]",
      isMobile 
        ? "bottom-4 left-4 right-4 w-auto" // Mobile : pleine largeur avec marges
        : "bottom-6 right-6 w-96 max-w-[calc(100vw-2rem)]" // Desktop : position normale à droite
    )}>
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-primary-600 text-white p-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Bot className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Adha</h3>
              <div className="flex items-center gap-1 text-sm text-white/90">
                {state.isConnected ? (
                  <>
                    <Wifi className="w-3 h-3" />
                    <span>Assistant IA • En ligne</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="w-3 h-3" />
                    <span>Mode hors ligne</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={resetConversation}
              className="text-white/80 hover:text-white transition-colors p-1"
              title="Nouvelle conversation"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <button
              onClick={minimizeChat}
              className="text-white/80 hover:text-white transition-colors p-1"
              title="Minimiser"
            >
              <Minimize2 className="w-4 h-4" />
            </button>
            <button
              onClick={toggleChat}
              className="text-white/80 hover:text-white transition-colors p-1"
              title="Fermer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Zone d'erreur */}
        {error && (
          <div className="bg-red-50 border-b border-red-200 p-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <span className="text-sm text-red-700 flex-1">{error}</span>
            <button
              onClick={clearError}
              className="text-red-500 hover:text-red-700 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Zone de messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-gray-50/50 to-white min-h-[300px] max-h-[400px]">
          {!state.currentConversation?.messages.length ? (
            <div className="text-center text-gray-500 mt-12">
              <Bot className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p className="text-sm font-medium">Bonjour ! Je suis Adha, votre assistant IA.</p>
              <p className="text-xs text-gray-400 mt-1">
                Comment puis-je vous aider avec votre entreprise ?
              </p>
            </div>
          ) : (
            <>
              {state.currentConversation.messages
                .filter(msg => msg && msg.id && msg.content) // Filtrer les messages invalides
                .map(renderMessage)}
              
              {/* Indicateur de frappe */}
              {state.isTyping && (
                <div className="flex gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-primary to-primary-600 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-gray-100 rounded-2xl px-4 py-3">
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Zone d'input */}
        <div className="p-4 border-t border-gray-200 bg-white flex-shrink-0">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              className={cn(
                "flex-1 px-4 py-3 border border-gray-300 rounded-xl",
                "focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary",
                "placeholder-gray-500 text-sm transition-colors",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
              placeholder={
                state.isConnected 
                  ? "Tapez votre message..." 
                  : "Mode hors ligne - messages sauvegardés"
              }
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              maxLength={500}
            />
            <button
              onClick={handleSend}
              disabled={!message.trim() || isLoading}
              className={cn(
                "px-4 py-3 bg-gradient-to-r from-primary to-primary-600 text-white rounded-xl",
                "hover:from-primary-hover hover:to-primary-700 transition-all duration-200",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                "focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2",
                "flex items-center justify-center"
              )}
              aria-label="Envoyer le message"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          
          {/* Compteur de caractères */}
          {message.length > 400 && (
            <div className="text-xs text-gray-500 mt-1 text-right">
              {message.length}/500
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdhaChat;